import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './TempoCounter.module.css';

interface Props {
  /** Current tempo, quarter notes per minute. */
  bpm: number;
  /** The tempo marked in the score. */
  marked: number;
  min: number;
  max: number;
  disabled?: boolean;
  onChange: (bpm: number) => void;
}

const HOLD_DELAY_MS = 400;
const HOLD_REPEAT_MS = 70;

/** A BPM counter: − and + (press and hold to keep going), a typed value, the percentage of the marked tempo, and a reset. */
export function TempoCounter({ bpm, marked, min, max, disabled, onChange }: Props) {
  const { t } = useTranslation();
  const [text, setText] = useState(String(bpm));
  const bpmRef = useRef(bpm);
  bpmRef.current = bpm;
  const holdRef = useRef<{ timeout: number; interval: number } | null>(null);
  const clamp = (v: number) => Math.min(max, Math.max(min, Math.round(v)));

  // Show the value from outside (buttons, reset) unless the user is typing something valid.
  useEffect(() => {
    setText((current) => (Number(current) === bpm ? current : String(bpm)));
  }, [bpm]);

  const stopHold = () => {
    if (!holdRef.current) return;
    window.clearTimeout(holdRef.current.timeout);
    window.clearInterval(holdRef.current.interval);
    holdRef.current = null;
  };
  useEffect(() => stopHold, []);

  const step = (delta: number) => onChange(clamp(bpmRef.current + delta));
  const startHold = (delta: number) => {
    stopHold();
    step(delta);
    const timeout = window.setTimeout(() => {
      if (holdRef.current) holdRef.current.interval = window.setInterval(() => step(delta), HOLD_REPEAT_MS);
    }, HOLD_DELAY_MS);
    holdRef.current = { timeout, interval: 0 };
  };

  const commitText = () => {
    const value = Number(text);
    if (Number.isFinite(value) && text.trim() !== '') onChange(clamp(value));
    setText(String(Number.isFinite(value) && text.trim() !== '' ? clamp(value) : bpmRef.current));
  };

  const percent = Math.round((bpm / marked) * 100);
  const stepper = (delta: number, label: string, symbol: string) => (
    <button
      type="button"
      className={styles.step}
      aria-label={label}
      data-tip={label}
      disabled={disabled || (delta < 0 ? bpm <= min : bpm >= max)}
      onPointerDown={(e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        startHold(delta);
      }}
      onPointerUp={stopHold}
      onPointerLeave={stopHold}
      onPointerCancel={stopHold}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          step(delta);
        }
      }}
    >
      {symbol}
    </button>
  );

  return (
    <div className={styles.tempo}>
      <span className={styles.label} id="tempo-label">
        {t('practice.tempo')}
      </span>
      <div className={styles.row}>
      <div className={styles.counter} role="group" aria-labelledby="tempo-label">
        {stepper(-1, t('practice.slower'), '−')}
        <label className={styles.value}>
          <input
            type="number"
            inputMode="numeric"
            min={min}
            max={max}
            step={1}
            value={text}
            disabled={disabled}
            aria-label={t('practice.tempoLabel')}
            onChange={(e) => {
              setText(e.target.value);
              const value = Number(e.target.value);
              if (e.target.value.trim() !== '' && Number.isFinite(value) && value >= min && value <= max) onChange(Math.round(value));
            }}
            onBlur={commitText}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitText();
            }}
          />
          <span className={styles.unit}>{t('practice.bpm')}</span>
        </label>
        {stepper(1, t('practice.faster'), '+')}
      </div>
      <span className={styles.percent} data-testid="tempo-percent">
        {t('practice.tempoPercent', { percent })}
      </span>
      {Math.round(marked) !== bpm && (
        <button
          type="button"
          className={styles.reset}
          aria-label={t('practice.resetTempo')}
          data-tip={t('practice.resetTempo')}
          disabled={disabled}
          onClick={() => onChange(clamp(marked))}
        >
          <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
            <path d="M3 12a9 9 0 1 0 2.64-6.36" />
            <path d="M3 3v5h5" />
          </svg>
        </button>
      )}
      </div>
    </div>
  );
}
